import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { ArrowLeft, Save, Loader2, Truck, ChevronsUpDown, Check } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import InventorySelector from '../../components/InventorySelector';

// Searchable location combobox
const LocationCombobox = ({ value, onChange, locations, placeholder, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm font-ibm ring-offset-background",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "hover:bg-accent/5 transition-colors",
            !value && "text-muted-foreground"
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[320px]" align="start">
        <Command>
          <CommandInput placeholder="Search location..." className="font-ibm" />
          <CommandList>
            <CommandEmpty>No location found.</CommandEmpty>
            <CommandGroup>
              {locations.map(loc => (
                <CommandItem
                  key={loc.id}
                  value={loc.location_name}
                  onSelect={(v) => {
                    onChange(v === value ? '' : v);
                    setOpen(false);
                  }}
                  className="font-ibm"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === loc.location_name ? "opacity-100" : "opacity-0")} />
                  <div>
                    <div className="font-medium">{loc.location_name}</div>
                    {loc.city && <div className="text-xs text-muted-foreground">{loc.city}, {loc.state}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const AddTrip = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = !!id;
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [availableDockets, setAvailableDockets] = useState([]);
  const [docketSearch, setDocketSearch] = useState('');
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [prevTripHint, setPrevTripHint] = useState(null);

  const [form, setForm] = useState(() => {
    // Pre-fill from URL params (booking confirmation flow)
    const params = new URLSearchParams(location.search);
    return {
      trip_date: new Date().toISOString().split('T')[0],
      vehicle_id: '',
      driver_id: '',
      conductor_id: '',
      trip_kind: params.get('trip_kind') || 'single_trip',
      trip_type: params.get('trip_type') || 'local',
      from_location: params.get('from') || '',
      to_location: params.get('to') || '',
      route: params.get('from') && params.get('to') ? `${params.get('from')} → ${params.get('to')}` : '',
      rate_type: '',
      rate_amount: 0,
      advance: 0,
      temperature_range: '',
      opening_balance: 0,
      remarks: params.get('from_booking') ? `Booking Ref: ${params.get('from_booking')}` : '',
      status: 'ACTIVE',
      docket_ids: [],
      inventory_items: [],
    };
  });

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [tripNumber, setTripNumber] = useState('');

  const rateTypes = [
    { value: 'minus_temp_rate', label: 'Minus Temp Rate' },
    { value: 'plus_temp_rate', label: 'Plus Temp Rate' },
    { value: 'general_rate', label: 'General Rate' },
    { value: 'cross_border_rate', label: 'Cross Border Rate' },
    { value: 'local_temp', label: 'Local Temp' },
    { value: 'local_gen', label: 'Local General' },
  ];

  const tripKindOptions = [
    { value: 'single_trip', label: 'Single Trip' },
    { value: 'round_trip', label: 'Round Trip' },
    { value: 'empty_trip', label: 'Empty Trip' },
  ];

  const tripTypeOptions = [
    { value: 'local', label: 'Local' },
    { value: 'national', label: 'National' },
    { value: 'international', label: 'International' },
  ];

  useEffect(() => {
    const excludeParam = isEdit ? `?exclude_trip_id=${id}` : '';
    const fetchMasters = async () => {
      try {
        // Detect super admin from token
        const token = localStorage.getItem('auth_token');
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.role === 'super_admin') {
              setIsSuperAdmin(true);
            }
          } catch {}
        }

        const [vehRes, drvRes, locRes, compRes] = await Promise.all([
          fetch(`${backendUrl}/api/trips/available-vehicles${excludeParam}`, { headers: getAuthHeaders() }),
          fetch(`${backendUrl}/api/trips/available-drivers${excludeParam}`, { headers: getAuthHeaders() }),
          fetch(`${backendUrl}/api/locations`, { headers: getAuthHeaders() }),
          fetch(`${backendUrl}/api/companies`, { headers: getAuthHeaders() }),
        ]);
        if (vehRes.ok) setVehicles(await vehRes.json());
        if (drvRes.ok) setDrivers(await drvRes.json());
        if (locRes.ok) setLocations(await locRes.json());
        if (compRes.ok) setCompanies(await compRes.json());
        // Dockets are fetched separately (company-dependent)
      } catch {
        toast({ title: "Error", description: "Failed to load master data", variant: "destructive" });
      }
    };
    fetchMasters();
  }, [backendUrl, getAuthHeaders, id, isEdit, toast]);

  // Re-fetch dockets whenever selectedCompanyId changes (super admin) or on initial load
  useEffect(() => {
    const excludeParam = isEdit ? `&exclude_trip_id=${id}` : '';
    const companyParam = selectedCompanyId ? `&company_id=${selectedCompanyId}` : '';
    fetch(`${backendUrl}/api/trips/available-dockets?_=1${excludeParam}${companyParam}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setAvailableDockets(data);
        // Clear previously selected dockets that don't belong to this company
        setForm(prev => ({ ...prev, docket_ids: [] }));
      });
  }, [backendUrl, getAuthHeaders, selectedCompanyId, id, isEdit]);

  useEffect(() => {
    if (!id) return;
    const fetchTrip = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${backendUrl}/api/trips/${id}`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setTripNumber(data.trip_number);
        if (data.company_id) setSelectedCompanyId(data.company_id);
        setForm({
          trip_date: data.trip_date || '',
          vehicle_id: data.vehicle_id || '',
          driver_id: data.driver_id || '',
          conductor_id: data.conductor_id || '',
          trip_kind: data.trip_kind || 'single_trip',
          trip_type: data.trip_type || 'local',
          from_location: data.from_location || '',
          to_location: data.to_location || '',
          route: data.route || '',
          rate_type: data.rate_type || '',
          rate_amount: data.rate_amount || 0,
          advance: data.advance || 0,
          temperature_range: data.temperature_range || '',
          opening_balance: data.opening_balance || 0,
          remarks: data.remarks || '',
          status: data.status || 'ACTIVE',
          docket_ids: (data.dockets || []).map(d => d.docket_id),
          inventory_items: data.inventory_items || [],
        });
      } catch {
        toast({ title: "Error", description: "Failed to load trip", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    fetchTrip();
  }, [id, backendUrl, getAuthHeaders, toast]);

  useEffect(() => {
    if (!form.vehicle_id) { setSelectedVehicle(null); return; }
    const v = vehicles.find(vh => vh.id === form.vehicle_id);
    setSelectedVehicle(v || null);
    if (v && form.rate_type) {
      const rate = v[form.rate_type];
      if (rate !== null && rate !== undefined) {
        setForm(prev => ({ ...prev, rate_amount: rate }));
      }
    }
  }, [form.vehicle_id, form.rate_type, vehicles]);

  // Auto-generate route from selected dockets
  useEffect(() => {
    if (form.docket_ids.length > 0) {
      const selectedDockets = availableDockets.filter(d => form.docket_ids.includes(d.id));
      const docketNos = selectedDockets.map(d => d.system_docket_no).join(', ');
      if (docketNos) {
        setForm(prev => ({ ...prev, route: prev.route || docketNos }));
      }
    }
  }, [form.docket_ids, availableDockets]);

  // Auto-fill opening balance from last completed trip when driver is selected (new trip only)
  useEffect(() => {
    if (!form.driver_id || isEdit) return;
    fetch(`${backendUrl}/api/trips/driver-previous-balance?driver_id=${form.driver_id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.opening_balance !== 0) {
          setForm(prev => ({ ...prev, opening_balance: data.opening_balance }));
          setPrevTripHint(data.previous_trip);
        } else {
          setForm(prev => ({ ...prev, opening_balance: 0 }));
          setPrevTripHint(null);
        }
      })
      .catch(() => {});
  }, [form.driver_id, backendUrl, getAuthHeaders, isEdit]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleDocket = (docketId) => {
    setForm(prev => ({
      ...prev,
      docket_ids: prev.docket_ids.includes(docketId)
        ? prev.docket_ids.filter(i => i !== docketId)
        : [...prev.docket_ids, docketId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.driver_id || !form.trip_date) {
      toast({ title: "Validation", description: "Trip Date, Vehicle and Driver are required", variant: "destructive" });
      return;
    }
    if (isSuperAdmin && !selectedCompanyId) {
      toast({ title: "Validation", description: "Please select a company for this trip", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        trip_date: form.trip_date,
        vehicle_id: form.vehicle_id,
        driver_id: form.driver_id,
        conductor_id: form.conductor_id || null,
        trip_kind: form.trip_kind,
        trip_type: form.trip_type,
        from_location: form.from_location,
        to_location: form.to_location,
        route: form.route || null,
        rate_type: form.rate_type || null,
        rate_amount: parseFloat(form.rate_amount) || 0,
        advance: Math.max(0, parseFloat(form.advance) || 0),
        temperature_range: form.temperature_range || null,
        opening_balance: parseFloat(form.opening_balance) || 0,
        remarks: form.remarks,
        docket_ids: form.docket_ids,
        inventory_item_ids: (form.inventory_items || []).map(i => i.id),
      };
      if (isSuperAdmin && selectedCompanyId) payload.company_id = selectedCompanyId;
      if (isEdit) payload.status = form.status;

      const url = isEdit ? `${backendUrl}/api/trips/${id}` : `${backendUrl}/api/trips`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save trip');
      }
      toast({ title: "Success", description: isEdit ? "Trip updated" : "Trip created" });
      navigate('/fleet/trips');
    } catch (err) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-accent-blue" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/fleet/trips')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 data-testid="trip-form-title" className="text-3xl font-bold text-text-dark font-manrope">
              {isEdit ? 'Edit Trip' : 'New Trip'}
            </h1>
            {tripNumber && <p className="text-accent-blue font-mono font-semibold mt-1">{tripNumber}</p>}
          </div>
        </div>
        <Button data-testid="save-trip-btn" onClick={handleSubmit} disabled={saving}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-8 rounded-lg">
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
          {isEdit ? 'Update Trip' : 'Create Trip'}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Trip Details */}
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark font-manrope mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-accent-blue" /> Trip Details
          </h2>

          {/* Company selector — visible only for super admin */}
          {isSuperAdmin && (
            <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="font-ibm text-sm font-semibold text-blue-800 mb-2 block">Company *</Label>
              <Select value={selectedCompanyId || '_none_'} onValueChange={v => setSelectedCompanyId(v === '_none_' ? '' : v)}>
                <SelectTrigger data-testid="trip-company" className="font-ibm bg-white max-w-md">
                  <SelectValue placeholder="Select Company..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">-- Select Company --</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name || c.company_name} {c.company_code ? `(${c.company_code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Trip Date *</Label>
              <Input data-testid="trip-date" type="date" value={form.trip_date}
                onChange={e => handleChange('trip_date', e.target.value)} className="font-ibm" />
            </div>

            {isEdit && (
              <div className="space-y-1.5">
                <Label className="font-ibm text-sm font-medium">Status</Label>
                <Select value={form.status} onValueChange={v => handleChange('status', v)}>
                  <SelectTrigger data-testid="trip-status" className="font-ibm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Vehicle *</Label>
              <Select value={form.vehicle_id} onValueChange={v => handleChange('vehicle_id', v)}>
                <SelectTrigger data-testid="trip-vehicle" className="font-ibm"><SelectValue placeholder="Select Vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.registration_number} {v.vehicle_name ? `- ${v.vehicle_name}` : ''} {v.vehicle_type_name ? `(${v.vehicle_type_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Driver *</Label>
              <Select value={form.driver_id} onValueChange={v => handleChange('driver_id', v)}>
                <SelectTrigger data-testid="trip-driver" className="font-ibm"><SelectValue placeholder="Select Driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} - {d.mobile_1} {d.vendor_name ? `(${d.vendor_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Conductor</Label>
              <Select value={form.conductor_id || '_none_'} onValueChange={v => handleChange('conductor_id', v === '_none_' ? '' : v)}>
                <SelectTrigger data-testid="trip-conductor" className="font-ibm"><SelectValue placeholder="Select Conductor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">No Conductor</SelectItem>
                  {drivers.filter(d => d.id !== form.driver_id).map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name} - {d.mobile_1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Trip Type (Radio-like) */}
            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Trip Type *</Label>
              <div className="flex gap-2 pt-1">
                {tripTypeOptions.map(opt => (
                  <button key={opt.value} type="button" data-testid={`trip-type-${opt.value}`}
                    onClick={() => handleChange('trip_type', opt.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-ibm font-medium border transition-colors ${
                      form.trip_type === opt.value
                        ? 'bg-accent-blue text-white border-accent-blue'
                        : 'bg-white text-text-secondary border-gray-200 hover:border-accent-blue/50'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Trip Kind (Radio-like) */}
            <div className="space-y-1.5 lg:col-span-2">
              <Label className="font-ibm text-sm font-medium">Trip</Label>
              <div className="flex gap-2 pt-1">
                {tripKindOptions.map(opt => (
                  <button key={opt.value} type="button" data-testid={`trip-kind-${opt.value}`}
                    onClick={() => handleChange('trip_kind', opt.value)}
                    className={`px-4 py-2 rounded-lg text-sm font-ibm font-medium border transition-colors ${
                      form.trip_kind === opt.value
                        ? 'bg-primary-blue text-white border-primary-blue'
                        : 'bg-white text-text-secondary border-gray-200 hover:border-primary-blue/50'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Opening Balance (Previous Dues)</Label>
              <Input data-testid="trip-opening-balance" type="number" value={form.opening_balance}
                onChange={e => handleChange('opening_balance', parseFloat(e.target.value) || 0)} className="font-ibm" />
              {prevTripHint ? (
                <p className="text-xs text-amber-600 font-ibm">
                  Auto-filled from: {prevTripHint.trip_number}
                  {prevTripHint.trip_date ? ` (${prevTripHint.trip_date})` : ''}
                  {prevTripHint.from_location && prevTripHint.to_location ? ` · ${prevTripHint.from_location} → ${prevTripHint.to_location}` : ''}
                </p>
              ) : (
                <p className="text-xs text-text-secondary font-ibm">+ve = credit to driver, −ve = driver owes</p>
              )}
            </div>
          </div>

          {/* From / To on the same row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">From *</Label>
              <LocationCombobox
                value={form.from_location}
                onChange={v => handleChange('from_location', v)}
                locations={locations}
                placeholder="Select origin..."
                testId="trip-from"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">To</Label>
              <LocationCombobox
                value={form.to_location}
                onChange={v => handleChange('to_location', v)}
                locations={locations}
                placeholder="Select destination..."
                testId="trip-to"
              />
            </div>
          </div>
        </Card>

        {/* Rates */}
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark font-manrope mb-4">Rates & Financial</h2>

          {selectedVehicle && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
              <p className="text-sm font-ibm text-blue-800 font-medium mb-2">Vehicle Rate Card: {selectedVehicle.registration_number}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs font-ibm">
                {rateTypes.map(rt => (
                  <div key={rt.value} className="bg-white rounded px-3 py-2 text-center">
                    <div className="text-text-secondary">{rt.label}</div>
                    <div className="font-semibold text-text-dark mt-1">{selectedVehicle[rt.value] ?? '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Temperature Range</Label>
              <Input data-testid="trip-temp-range" value={form.temperature_range}
                onChange={e => handleChange('temperature_range', e.target.value)} placeholder="e.g. -20 to +5" className="font-ibm" />
            </div>

            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Rate Type</Label>
              <Select value={form.rate_type || '_none_'} onValueChange={v => handleChange('rate_type', v === '_none_' ? '' : v)}>
                <SelectTrigger data-testid="trip-rate-type" className="font-ibm"><SelectValue placeholder="Select Rate Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">None</SelectItem>
                  {rateTypes.map(rt => <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Rate Amount</Label>
              <Input data-testid="trip-rate-amount" type="number" value={form.rate_amount}
                onChange={e => handleChange('rate_amount', e.target.value)} className="font-ibm" />
            </div>

            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Advance to Driver</Label>
              <Input data-testid="trip-advance" type="number" min="0" value={form.advance}
                onChange={e => handleChange('advance', Math.max(0, parseFloat(e.target.value) || 0))} className="font-ibm" />
              <p className="text-xs text-text-secondary font-ibm">Cash given upfront — counted in net balance</p>
            </div>
          </div>
        </Card>

        {/* DWB / Docket Assignment */}
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-text-dark font-manrope">
              DWB No. (Assign Dockets)
              <Badge className="ml-2 bg-accent-blue/10 text-accent-blue border-0 font-ibm">{form.docket_ids.length} selected</Badge>
            </h2>
            <Input 
              type="text" 
              placeholder="Search docket..." 
              value={docketSearch} 
              onChange={e => setDocketSearch(e.target.value)}
              className="max-w-xs h-9 text-sm"
            />
          </div>
          {availableDockets.length === 0 ? (
            <p className="text-sm text-text-secondary font-ibm py-4">No available dockets.</p>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[250px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase font-ibm w-12"></th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase font-ibm">Docket No.</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase font-ibm">Type</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-text-secondary uppercase font-ibm">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {availableDockets.filter(d => !docketSearch || (d.system_docket_no && d.system_docket_no.toLowerCase().includes(docketSearch.toLowerCase()))).map(d => {
                    const isChecked = form.docket_ids.includes(d.id);
                    return (
                      <tr key={d.id} className={`cursor-pointer transition-colors ${isChecked ? 'bg-accent-blue/5' : 'hover:bg-gray-50'}`}
                        onClick={() => toggleDocket(d.id)}>
                        <td className="px-4 py-2.5">
                          <input type="checkbox" checked={isChecked} readOnly data-testid={`docket-check-${d.id}`}
                            className="w-4 h-4 rounded border-gray-300 text-accent-blue" />
                        </td>
                        <td className="px-4 py-2.5 text-sm font-mono font-medium text-text-dark">{d.system_docket_no}</td>
                        <td className="px-4 py-2.5 text-sm text-text-secondary font-ibm">{d.shipment_type || '—'}</td>
                        <td className="px-4 py-2.5"><Badge className="bg-gray-100 text-gray-700 border-0 text-xs font-ibm">{d.status}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Inventory Items (multi-add) */}
        <InventorySelector
          value={form.inventory_items}
          onChange={(next) => setForm(prev => ({ ...prev, inventory_items: next }))}
          excludeTripId={isEdit ? id : undefined}
        />

        {/* Route & Remarks */}
        <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Route</Label>
              <Textarea data-testid="trip-route" value={form.route}
                onChange={e => handleChange('route', e.target.value)}
                placeholder="Auto-generated from dockets or enter manually" className="font-ibm min-h-[80px]" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-ibm text-sm font-medium">Remarks</Label>
              <Textarea data-testid="trip-remarks" value={form.remarks}
                onChange={e => handleChange('remarks', e.target.value)}
                placeholder="Additional notes..." className="font-ibm min-h-[80px]" />
            </div>
          </div>
        </Card>
      </form>
    </div>
  );
};

export default AddTrip;
